import styled from "styled-components"
import { maxWidth, minWidth } from "utils"

// Main container that handles the expansion/collapse transition
export const SliderContainer = styled.div`
  display: flex;
  flex-direction: row;
  height: 44.5rem;
  background: ${({ theme }) => theme.component?.background || "#e2f4f9"};
  color: ${({ theme }) => theme.component?.text || theme.colors.text.primary};
  position: relative;
  overflow: hidden;
  transition: none;

  ${maxWidth.desktopL`
    height: 40rem;
  `}

  ${maxWidth.tablet`
    height: auto;
    padding: 2rem;
  `}

  ${maxWidth.mobile`
    padding: 1.5rem;
  `}
`

// Image section that transitions smoothly
export const SliderMediaSection = styled.div`
  flex: 0 0 34.5rem;
  height: 44.5rem;
  position: relative;
  background: ${({ theme }) =>
    theme.component?.background || theme.colors.background.primary || "red"};
  overflow: hidden;
  padding: 6rem 3.5rem;
  transition: none;

  ${maxWidth.desktopL`
      flex: 0 0 30rem;
      height: 100%;
  `}

  ${({ backgroundImage }) =>
    backgroundImage &&
    `
    &::before {
      content: "";
      position: absolute;
      top: 6rem;
      left: 3.5rem;
      right: 3.5rem;
      bottom: 6rem;
      background: linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url(${backgroundImage});
      background-size: cover;
      background-position: center;
      border-radius: 1.2rem;
      z-index: 1;
      transition: none;
    }
  `}

  ${maxWidth.desktopL`
    ${({ backgroundImage }) =>
      backgroundImage &&
      `
      &::before {
        top: 5rem;
        left: 3rem;
        right: 3rem;
        bottom: 5rem;
      }
    `}
  `}

  ${maxWidth.hamburgerLarge`
    ${({ backgroundImage }) =>
      backgroundImage &&
      `
      &::before {
        top: 4.5rem;
        left: 2.5rem;
        right: 2.5rem;
        bottom: 4.5rem;
      }
    `}
  `}

  ${maxWidth.tablet`
    height: 25rem;
    padding: 3.5rem 2rem;

    ${({ backgroundImage }) =>
      backgroundImage &&
      `
      &::before {
        top: 3.5rem;
        left: 2rem;
        right: 2rem;
        bottom: 3.5rem;
      }
    `}
  `}

  ${maxWidth.mobile`
    height: 20rem;
    padding: 2.5rem 1.5rem;

    ${({ backgroundImage }) =>
      backgroundImage &&
      `
      &::before {
        top: 2.5rem;
        left: 1.5rem;
        right: 1.5rem;
        bottom: 2.5rem;
      }
    `}
  `}
`

export const SliderVideo = styled.video`
  object-fit: cover;
  border-radius: 1.2rem;
  height: 100%;
  width: 100%;
`

// Content section that appears/disappears smoothly
export const SliderContentSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: 5rem 4rem 5rem 0;
  background: ${({ theme }) => theme.component?.background || "#e2f4f9"};
  justify-content: space-between;

  ${maxWidth.desktopL`
    padding: 3rem 1.5rem;
  `}

  ${maxWidth.hamburgerLarge`
    padding: 2.5rem 1.5rem;
  `}

  ${maxWidth.tablet`
    padding: 2rem 1rem;
  `}

  ${maxWidth.mobile`
    padding: 1.5rem 0.8rem;
  `}
`

export const SliderContent = styled.div`
  flex: 0 0 auto;
  width: 100%;
`

export const SliderPreHeadingContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;

  ${maxWidth.mobile`
    margin-bottom: 0;
  `}
`

export const SliderPreHeadingText = styled.div`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.component?.highlight || theme.colors.text.primary};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.2rem;

  ${maxWidth.desktopL`
    font-size: 1.1rem;
  `}

  ${maxWidth.hamburgerLarge`
    font-size: 1rem;
  `}

  ${maxWidth.tablet`
    font-size: 0.9rem;
  `}

  ${maxWidth.mobile`
    font-size: 1rem;
    margin-bottom: 0;
    color: ${({ sliderIndex }) => (sliderIndex === 0 ? "#222222" : "#ffffff")};
  `}
`

export const SliderPreHeadingLogo = styled.img`
  height: 1.6rem;
  width: auto;
`

export const SliderHeading = styled.h2`
  font-size: 6.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.component?.text || theme.colors.text.primary};
  letter-spacing: -0.06rem;

  ${maxWidth.desktopL`
    font-size: 4.8rem;
  `}

  ${maxWidth.hamburgerLarge`
    font-size: 4.5rem;
  `}

  ${maxWidth.tablet`
    font-size: 3.8rem;
  `}

  ${maxWidth.mobile`
    font-size: 3.2rem;
    line-height: 1;
    color: ${({ theme }) => theme.component?.text || theme.colors.text.primary};
    font-weight: 700;
  `}
`

export const SliderBody = styled.div`
  font-size: 1.9rem;
  line-height: 1.5;
  width: 70%;
  color: ${({ sliderIndex }) => (sliderIndex === 0 ? "#222" : "#ffffff")};

  ${maxWidth.desktopL`
    font-size: 1.6rem;
    width: 75%;
  `}

  ${maxWidth.hamburgerLarge`
    font-size: 1.5rem;
    width: 80%;
  `}

  ${maxWidth.tablet`
    font-size: 1.3rem;
    width: 85%;
  `}

  ${maxWidth.mobile`
    font-size: 1.4rem;
    line-height: 1.4;
    width: 100%;
    color: ${({ sliderIndex }) => (sliderIndex === 0 ? "#222222" : "#ffffff")};
  `}
`

export const SliderCardsRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1.8rem;
  margin-top: 2rem;

  ${minWidth.tablet`
    width: 46rem;
  `}

  ${minWidth.desktop`
    width: 38rem;
  `}

  ${minWidth.rightRail`
    width: 48rem;
  `}

  ${minWidth.desktopL`
    width: 50rem;
  `}

  @media (min-width: 1248px) {
    width: 58rem;
  }

  ${minWidth.desktopXL`
    width: 62rem;
  `}

  ${minWidth.largest`
    width: 68rem;
  `}
`

export const SliderCard = styled.div`
  padding-top: 1rem;
  border-top: 1px solid
    ${({ sliderIndex }) => (sliderIndex === 0 ? "#e0e0e0" : "rgba(255, 255, 255, 0.2)")};
`

export const SliderCardTitle = styled.h3`
  font-size: 1.8rem;

  font-weight: normal;
  line-height: 1.3;
  margin-bottom: 0.8rem;

  color: ${({ sliderIndex }) => (sliderIndex === 0 ? "#222222" : "#ffffff")};

  ${maxWidth.desktopL`
    font-size: 1.6rem;
  `}

  ${maxWidth.hamburgerLarge`
    font-size: 1.5rem;
  `}

  ${maxWidth.tablet`
    font-size: 1.3rem;
  `}

  ${maxWidth.mobile`
    font-size: 1.2rem;
    line-height: 1.3;
    margin-bottom: 0.4rem;
    color: ${({ sliderIndex }) => (sliderIndex === 0 ? "#222222" : "#ffffff")};
    font-weight: normal;
  `}
`

export const SliderCardAuthor = styled.p`
  font-size: 1.4rem;
  word-wrap: break-word;
  overflow-wrap: break-word;
  color: ${({ sliderIndex }) => (sliderIndex === 0 ? "#222222" : "#ffffff")};
  margin: 0;

  ${maxWidth.tablet`
    font-size: 1.1rem;
  `}

  ${maxWidth.mobile`
    font-size: 1.2rem;
    color: ${({ sliderIndex }) => (sliderIndex === 0 ? "#222222" : "#ffffff")};
    opacity: 0.7;
  `}
`

export const SliderButton = styled.button`
  border-radius: 50px;
  background-color: ${({ theme }) => theme.component?.cta || "initial"};
  color: ${({ theme }) => theme.colors?.text?.alternate || "#ffffff"};
  border: none;
  padding: 1.8rem 4.9rem;
  font-size: 1.3rem;
  font-weight: 500;
  letter-spacing: 0.05rem;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;
  width: fit-content;

  ${maxWidth.desktopL`
    padding: 1.6rem 4.2rem;
    font-size: 1.2rem;
  `}

  ${maxWidth.hamburgerLarge`
    padding: 1.4rem 3.8rem;
    font-size: 1.1rem;
  `}

  ${maxWidth.tablet`
    padding: 1.2rem 3.2rem;
    font-size: 1rem;
  `}

  ${maxWidth.mobile`
    width: 12.2rem;
    height: 2.2rem;
    font-size: 1rem;
    font-weight: 500;
    letter-spacing: 0.05rem;
    padding: 0.5rem 1rem;
  `}
`

export const SliderCollapsedOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  flex-direction: column;
  justify-content: space-between;
  display: flex;
  align-items: center;
  padding: 0;
  z-index: 10;
  opacity: ${({ isExpanded }) => (isExpanded ? 0 : 1)};
  pointer-events: ${({ isExpanded }) => (isExpanded ? "none" : "auto")};
  transition: opacity 0s linear;
  transition-delay: ${({ isExpanded }) => (isExpanded ? "0s" : "0.6s")};

  ${maxWidth.tablet`
    display: none;
  `}
`

export const SliderCollapsedTitle = styled.h2`
  position: absolute;
  top: 6rem;
  left: 3.5rem;
  right: 3.5rem;
  bottom: 6rem;
  color: ${({ theme }) => theme.colors?.text?.alternate || "#ffffff"};
  font-size: 3.6rem;
  font-weight: 700;
  text-align: center;
  line-height: 1.2;
  z-index: 2;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: none;

  ${maxWidth.desktopL`
    top: 5rem;
    left: 3rem;
    right: 3rem;
    bottom: 5rem;
    font-size: 3.2rem;
  `}

  ${maxWidth.hamburgerLarge`
    top: 4.5rem;
    left: 2.5rem;
    right: 2.5rem;
    bottom: 4.5rem;
    font-size: 3rem;
  `}

  ${maxWidth.tablet`
    top: 3.5rem;
    left: 2rem;
    right: 2rem;
    bottom: 3.5rem;
    font-size: 2.6rem;
  `}

  ${maxWidth.mobile`
    top: 2.5rem;
    left: 1.5rem;
    right: 1.5rem;
    bottom: 2.5rem;
    font-size: 2.4rem;
  `}
`

export const SliderTitleTop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 1.2rem;
  font-weight: 600;
  color: ${({ sliderIndex }) => (sliderIndex === 0 ? "#2260b2" : "#ffffff")};
  letter-spacing: 0.5px;
  margin: 0;
  z-index: 3;
  text-transform: uppercase;

  ${maxWidth.desktopL`
    height: 5.5rem;
    font-size: 1.1rem;
  `}

  ${maxWidth.hamburgerLarge`
    height: 4.5rem;
    font-size: 1.1rem;
  `}

  ${maxWidth.tablet`
    height: 4rem;
    font-size: 1rem;
  `}

  ${maxWidth.mobile`
    height: 3rem;
    font-size: 1rem;
  `}
`

export const SliderTitleBottom = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 1.2rem;
  font-weight: 600;
  color: ${({ sliderIndex }) => (sliderIndex === 0 ? "#2260b2" : "#ffffff")};
  letter-spacing: 0.5px;
  margin: 0;
  line-height: 1;
  z-index: 3;
  text-transform: uppercase;

  ${maxWidth.desktopL`
    height: 5.5rem;
    font-size: 1.1rem;
  `}

  ${maxWidth.hamburgerLarge`
    height: 4.5rem;
    font-size: 1.1rem;
  `}

  ${maxWidth.tablet`
    height: 4rem;
    font-size: 1rem;
  `}

  ${maxWidth.mobile`
    height: 3rem;
    font-size: 1rem;
  `}
`

// Mobile specific styles (keeping existing mobile implementation)
export const SliderMobileStackedContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  padding: 3rem 2rem;
  background: ${({ theme }) => theme.component?.background || "#e2f4f9"};
  color: ${({ sliderIndex }) => (sliderIndex === 0 ? "#222222" : "#ffffff")};

  ${maxWidth.hamburgerSmall`
    padding: 3rem 2rem;
    min-height: unset;
    gap: 2rem;
    flex-direction: column;
  `}
`

export const SliderMobileTopRow = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: 2rem;

  ${maxWidth.hamburgerSmall`
    gap: 3rem;
  `}

  ${maxWidth.mobile`
    gap: 2rem;
    align-items: flex-start;
  `}
`

export const SliderMobileImageSection = styled.div`
  flex: 0 0 40%;
  height: 100%;
  position: relative;
  background: ${({ backgroundImage, theme }) =>
    backgroundImage ? `url(${backgroundImage})` : theme.component?.background || "#f0f0f0"};
  background-size: cover;
  background-position: center;
  border-radius: 1.2rem;
  min-height: 25rem;

  ${maxWidth.desktopL`
    flex: 0 0 35%;
    min-height: 22rem;
  `}

  ${maxWidth.hamburgerLarge`
    flex: 0 0 30%;
    min-height: 20rem;
  `}

  ${maxWidth.tablet`
    flex: 0 0 25%;
    min-height: 18rem;
  `}

  ${maxWidth.mobile`
    width: 12.6rem;
    height: 15.7rem;
    min-height: unset;
    flex: 0 0 12.6rem;
    border-radius: 1.2rem;
  `}
`

export const SliderMobileContentSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  z-index: 2;
  position: relative;
  color: ${({ sliderIndex }) => (sliderIndex === 0 ? "#222222" : "#ffffff")};

  ${maxWidth.desktopL`
    gap: 0.4rem;
  `}

  ${maxWidth.hamburgerLarge`
    gap: 0.3rem;
  `}

  ${maxWidth.tablet`
    gap: 0.2rem;
  `}

  ${maxWidth.mobile`
    gap: 0.5rem;
  `}
`

export const SliderMobileCardsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
  padding-top: 2rem;

  ${maxWidth.tablet`
    padding-top: 1.2rem;
  `}

  ${maxWidth.hamburgerSmall`
    flex-direction: row;
    gap: 2rem;
  `}
`

export const SliderMobileCard = styled.div`
  flex: 1;
  padding: 1rem 0;
  border-top: 0.1rem solid rgba(255, 255, 255, 0.2);

  ${maxWidth.desktopL`
    padding: 0.8rem 0;
  `}

  ${maxWidth.hamburgerLarge`
    padding: 0.7rem 0;
  `}

  ${maxWidth.tablet`
    padding: 0.6rem 0;
  `}

  ${maxWidth.hamburgerSmall`
    padding: 1rem 0 0;
    border-top: 0.1rem solid rgba(0, 0, 0, 0.1);
    flex: 1;
  `}
`
